import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';

const AdminContacts = () => {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Respond modal state
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyMsg, setReplyMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const data = await api.get(`/admin/contact-submissions?${params}`);
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(err.message || 'Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openRespond = (sub) => {
    setSelected(sub);
    setReply('');
    setReplyMsg('');
  };

  const closeModal = () => {
    setSelected(null);
    setReply('');
    setReplyMsg('');
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setReplyLoading(true);
    setReplyMsg('');
    try {
      await api.post(`/admin/contact-submissions/${selected.id}/respond`, { reply });
      setReplyMsg('success');
      setSubmissions(prev => prev.map(s => s.id === selected.id ? { ...s, status: 'responded' } : s));
      setTimeout(closeModal, 1500);
    } catch (err) {
      setReplyMsg(err.message || 'Failed to send reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const formatDate = (dt) => dt ? new Date(dt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <AdminLayout>
      <main className="al-content">
        <div className="page-header">
          <h1>Contact Submissions</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 4 }}>{total} total submission{total !== 1 ? 's' : ''}</p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {['', 'new', 'responded'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                borderColor: statusFilter === s ? '#0A1F4E' : '#e2e8f0',
                background: statusFilter === s ? '#0A1F4E' : '#fff',
                color: statusFilter === s ? '#fff' : '#475569',
                fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer'
              }}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button onClick={load} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>
            ↻ Refresh
          </button>
        </div>

        {/* Table */}
        <div className="recent-section" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>{error}</div>
          ) : submissions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No contact submissions found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Name', 'Email', 'Subject', 'Message', 'Received', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub, i) => (
                    <tr key={sub.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, whiteSpace: 'nowrap' }}>{sub.full_name}</td>
                      <td style={{ padding: '10px 14px', color: '#1043A0' }}>
                        <a href={`mailto:${sub.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{sub.email}</a>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{sub.subject}</td>
                      <td style={{ padding: '10px 14px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#64748b' }} title={sub.message}>
                        {sub.message}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#94a3b8', fontSize: '0.8rem' }}>{formatDate(sub.created_at)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700,
                          background: sub.status === 'responded' ? '#dcfce7' : '#fef3c7',
                          color: sub.status === 'responded' ? '#166534' : '#92400e'
                        }}>
                          {sub.status === 'responded' ? 'Responded' : 'New'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <button
                          onClick={() => openRespond(sub)}
                          style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#0A1F4E', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                        >
                          {sub.status === 'responded' ? 'View / Reply' : 'Reply'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1.25rem' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 14px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}>← Prev</button>
            <span style={{ padding: '6px 12px', color: '#475569', fontSize: '0.875rem' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 14px', borderRadius: 6, border: '1.5px solid #e2e8f0', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}>Next →</button>
          </div>
        )}
      </main>

      {/* Reply Modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0A1F4E' }}>Reply to {selected.full_name}</h2>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
            </div>

            {/* Original message */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '1rem', marginBottom: '1.25rem', borderLeft: '3px solid #e2e8f0', maxWidth: '100%', overflow: 'hidden' }}>
              <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                {selected.subject} — {formatDate(selected.created_at)}
              </p>
              <p style={{ margin: 0, color: '#334155', fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{selected.message}</p>
              {selected.phone && <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.82rem' }}>📞 {selected.phone}</p>}
            </div>

            {replyMsg === 'success' ? (
              <div style={{ padding: '1rem', background: '#dcfce7', borderRadius: 8, color: '#166534', fontWeight: 600, textAlign: 'center' }}>
                ✅ Reply sent successfully!
              </div>
            ) : (
              <form onSubmit={handleSendReply}>
                {replyMsg && <div style={{ padding: '10px 14px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', marginBottom: '1rem', fontSize: '0.875rem' }}>{replyMsg}</div>}
                <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, fontSize: '0.875rem', color: '#334155' }}>
                  Your reply (will be sent to {selected.email})
                </label>
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  rows={6}
                  placeholder="Type your reply here…"
                  required
                  style={{ width: '100%', padding: '0.75rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={closeModal} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                  <button type="submit" disabled={replyLoading || !reply.trim()} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#CF142B', color: '#fff', fontWeight: 700, cursor: replyLoading ? 'wait' : 'pointer', opacity: !reply.trim() ? 0.6 : 1 }}>
                    {replyLoading ? 'Sending…' : 'Send Reply'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;
